import apiClient, { scheduleProactiveTokenRefresh, setAccessToken, setRefreshToken, clearTokens } from './apiClient';
import { hashPasswordForTransmission } from '../utils/crypto';


export interface LoginRequest {
    email: string;
    password: string;
    two_factor_code?: string;
}

export interface LoginResponse {
    success: true;
    data: {
        accessToken: string | null;
        refreshToken: string | null;
        tokenType: string;
        expiresIn: number;
        user: {
            userId: string;
            email: string;
            firstName: string;
            lastName: string;
            role: string;
            preferredLanguage: string | null;
            createdAt: string | null;
        } | null;
        // ===== NEW: 2FA fields =====
        twoFactorRequired?: boolean;
        tempToken?: string;
    };
}

export interface RegisterRequest {
    email: string;
    password: string;
    fullName: string;
    preferred_language?: string;
    /** Co-applicant invitation token — when present, the backend skips email verification */
    invitation_token?: string;
    /** Partner code from ?partner= URL param — records which platform referred this registration */
    partner_code?: string;
}

export interface RegisterResponse {
    success: true;
    data: {
        userId?: string;
        user_id?: string;
        email: string;
        email_verification_required?: boolean;
        verification_email_sent?: boolean;
        verificationRequired?: boolean;
        /** Present when the invitation fast-path ran — use to auto-login without a separate /login call */
        accessToken?: string;
        refreshToken?: string;
        message?: string;
    };
}

export interface PasswordResetRequest {
    email: string;
}

export interface PasswordResetResponse {
    success: boolean;
    data?: {
        message: string;
    };
    error?: {
        code: string;
        message: string;
        details?: any;
    };
}

export interface PasswordResetConfirmRequest {
    token: string;
    new_password: string;
}

export interface PasswordResetConfirmResponse {
    success: true;
    data: {
        message: string;
    };
}

export interface ChangePasswordRequest {
    current_password: string;
    new_password: string;
}

export interface ChangePasswordResponse {
    success: true;
    data: {
        message: string;
    };
}

// ===== NEW: 2FA response types =====

export interface TwoFactorSetupResponse {
    success: true;
    data: {
        secret: string;
        qrCode: string;
    };
}

export interface TwoFactorStatusResponse {
    success: true;
    data: {
        enabled: boolean;
    };
}

const authService = {
    /**
     * Login user
     * POST /auth/login
     * 
     * When 2FA is enabled, returns twoFactorRequired=true and tempToken.
     * Call validateTwoFactor() to complete the login.
     */
    async login(credentials: LoginRequest): Promise<LoginResponse> {
        // Hash password before transmission
        const hashedPassword = await hashPasswordForTransmission(credentials.password);
        const response = await apiClient.post<LoginResponse>('/auth/login', {
            ...credentials,
            password: hashedPassword,
        });

        // Only store tokens if login is fully complete (no 2FA challenge)
        if (!response.data.data.twoFactorRequired && response.data.data.accessToken) {
            const { accessToken, refreshToken } = response.data.data;
            setAccessToken(accessToken);
            setRefreshToken(refreshToken!);
            scheduleProactiveTokenRefresh(accessToken);
        }

        return response.data;
    },

    /**
     * Register new customer
     * POST /customers/register
     */
    async register(userData: RegisterRequest): Promise<RegisterResponse> {
        // Hash password before transmission
        const hashedPassword = await hashPasswordForTransmission(userData.password);
        const response = await apiClient.post<RegisterResponse>('/customers/register', {
            ...userData,
            password: hashedPassword,
        });
        return response.data;
    },

    /**
     * Logout user
     * POST /auth/logout
     */
    async logout(): Promise<void> {
        try {
            await apiClient.post('/auth/logout');
        } finally {
            // Clear tokens regardless of API response
            clearTokens();
        }
    },

    /**
     * Refresh access token
     * POST /auth/refresh
     */
    async refreshToken(): Promise<LoginResponse> {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await apiClient.post<LoginResponse>('/auth/refresh', {
            refresh_token: refreshToken,
        });

        // Update tokens
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        setAccessToken(accessToken!);
        setRefreshToken(newRefreshToken!);

        return response.data;
    },

    /**
     * Request password reset
     * POST /auth/password-reset
     * 
     * Only sends email, no password involved yet.
     */
    async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
        const response = await apiClient.post<PasswordResetResponse>('/auth/password-reset', { email });
        return response.data;
    },

    /**
     * Confirm password reset with new password
     * POST /auth/password-reset/confirm
     */
    async confirmPasswordReset(token: string, newPassword: string): Promise<PasswordResetConfirmResponse> {
        // Hash password before transmission
        const hashedPassword = await hashPasswordForTransmission(newPassword);
        const response = await apiClient.post<PasswordResetConfirmResponse>('/auth/password-reset/confirm', {
            token,
            new_password: hashedPassword,
        });
        return response.data;
    },

    /**
     * Change password for authenticated user
     * POST /auth/change-password
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
        // Hash passwords before transmission
        const hashedCurrentPassword = await hashPasswordForTransmission(currentPassword);
        const hashedNewPassword = await hashPasswordForTransmission(newPassword);
        const response = await apiClient.post<ChangePasswordResponse>('/auth/change-password', {
            current_password: hashedCurrentPassword,
            new_password: hashedNewPassword,
        });
        return response.data;
    },

    /**
     * Verify email with token
     * POST /customers/verify-email
     */
    async verifyEmail(token: string): Promise<{ success: boolean; data: { email_verified: boolean; message: string } }> {
        const response = await apiClient.post('/customers/verify-email', {
            verification_token: token,
        });
        return response.data;
    },

    /**
     * Resend verification email
     * POST /customers/resend-verification
     */
    async resendVerificationEmail(email: string): Promise<{ success: boolean; data: { message: string } }> {
        const response = await apiClient.post('/customers/resend-verification', { email });
        return response.data;
    },

    // =========================================================================
    // Two-Factor Authentication (NEW)
    // =========================================================================

    /**
     * Validate 2FA code during login (phase 2)
     * POST /auth/2fa/validate
     * 
     * Called after login() returns twoFactorRequired=true.
     * Completes the login and returns full tokens + user data.
     */
    async validateTwoFactor(tempToken: string, code: string): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>('/auth/2fa/validate', {
            tempToken,
            code,
        });

        // Store tokens after successful 2FA
        if (response.data.data.accessToken) {
            const { accessToken, refreshToken } = response.data.data;
            setAccessToken(accessToken);
            setRefreshToken(refreshToken!);
            scheduleProactiveTokenRefresh(accessToken);
        }

        return response.data;
    },

    /**
     * Start 2FA setup — get QR code and secret
     * POST /auth/2fa/setup
     * 
     * Returns a QR code data URI to display in an <img> tag
     * and the Base32 secret for manual entry.
     */
    async setup2FA(): Promise<{ secret: string; qrCode: string }> {
        const response = await apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup');
        return response.data.data;
    },

    /**
     * Verify 2FA setup with a code from the authenticator app
     * POST /auth/2fa/verify
     * 
     * Activates 2FA on the account if the code is valid.
     */
    async verify2FA(code: string): Promise<{ enabled: boolean; message: string }> {
        const response = await apiClient.post('/auth/2fa/verify', { code });
        return response.data.data;
    },

    /**
     * Disable 2FA
     * POST /auth/2fa/disable
     */
    async disable2FA(): Promise<void> {
        await apiClient.post('/auth/2fa/disable');
    },

    /**
     * Check current 2FA status
     * GET /auth/2fa/status
     */
    async get2FAStatus(): Promise<{ enabled: boolean }> {
        const response = await apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status');
        return response.data.data;
    },
};

export default authService;